import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { RolesWeb } from 'src/app/core/enums/roles.enum';
import { SessionCacheModel } from 'src/app/core/models/session-cache.model';
import { User } from 'src/app/core/models/user';
import { UserResponsibleModel } from 'src/app/core/models/user-responsible.model';
import { NotificationService } from 'src/app/core/services/notification.service';
import { SessionCacheService } from 'src/app/core/services/session-cache.service';
import { UserService } from 'src/app/core/services/user.services';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-responsible',
  templateUrl: './responsible.component.html',
  styleUrls: ['./responsible.component.scss']
})
export class ResponsibleComponent implements OnInit, OnDestroy {
  private readonly RESPONSABLES: string = 'responsibles';
  private readonly TO_MILISECONDS: number = environment.throttledRefreshBalance * 6000;
  private sessionDataSubscription: Subscription;

  loading: boolean = false;
  loggedUser: User;
  searchResp = new UntypedFormControl('');
  filterResp: UserResponsibleModel[] = [];
  respActual: number = 1;
  responsiblesList: UserResponsibleModel[];
  userModel: UserResponsibleModel;
  loadingData: boolean = false;

  RolesWeb = {
    RESPONSABLE: 'Responsable',
    FINANCIERO: 'Financiero',
    COMERCIO: 'Comercio'
  };

  constructor(
    private sessionCacheService: SessionCacheService,
    private userService: UserService,
    private router: Router,
    private notificationService: NotificationService
  ) {
    this.userModel = new UserResponsibleModel();
  }

  async ngOnInit(): Promise<void> {
    await this.loadResponsibles();

    this.searchResp.valueChanges.pipe(debounceTime(150)).subscribe((value) => {
      if (value) {
        this.respActual = 1;
        this.filterResp = this.responsiblesList.filter(
          (branch) =>
            branch.nombre.toUpperCase().includes(value.toUpperCase()) ||
            branch.apellido.toUpperCase().includes(value.toUpperCase())
        );
      } else {
        this.respActual = 1;
        this.filterResp = this.responsiblesList.filter((cashier) => cashier.estado !== 'B');
      }
    });
    if (!this.responsiblesList) {
      this.responsiblesList = [];
    }

    this.sessionDataSubscription = this.sessionCacheService.sessionData$.subscribe((deletedKey: string | null) => {
      if (deletedKey === 'responsibles') {
        this.handleSessionDataChange();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sessionDataSubscription) {
      this.sessionDataSubscription.unsubscribe();
    }
  }

  async loadResponsibles(): Promise<void> {
    this.sessionCacheService.expiredData(this.RESPONSABLES);
    !this.sessionCacheService.getData(this.RESPONSABLES)
      ? await this.saveResponsiblesInCache()
      : this.getResponsiblesInCache();
    !this.responsiblesList ? (this.responsiblesList = []) : null;
  }

  async saveResponsiblesInCache(): Promise<void> {
    let cache = new SessionCacheModel();
    this.responsiblesList = await this.userService.getUserResponsible();
    this.filterResp = this.responsiblesList.filter((cashier) => cashier.estado !== 'B');
    cache.data = this.responsiblesList;
    cache.iat = +new Date() + this.TO_MILISECONDS;
    this.sessionCacheService.saveDataWithExpiration(this.RESPONSABLES, cache);
  }

  getResponsiblesInCache(): void {
    this.responsiblesList = JSON.parse(this.sessionCacheService.getData(this.RESPONSABLES)).data;
    this.filterResp = this.responsiblesList.filter((cashier) => cashier.estado !== 'B');
  }

  editClickEventHandler(userResponsible: any): void {
    if (userResponsible.id) {
      this.userModel.usuarioId = userResponsible.id;
    } else {
      this.userModel.usuarioId = userResponsible.usuarioId;
    }
    this.goToEditResponsible(this.userModel);
  }

  goToEditResponsible(data: any): void {
    this.goToAgregarUsuario(true);
  }

  private async handleSessionDataChange() {
    await this.loadResponsibles();
  }

  goToAgregarUsuario(isEdition: boolean = false): void {
    isEdition
      ? this.router.navigate(['/user/my-commerce/responsible/edit'], {
          queryParams: { responsible: this.userModel.usuarioId }
        })
      : this.router.navigate(['/user/my-commerce/responsible/create']);
  }

  async removeClickEventHandler(userResponsible: UserResponsibleModel): Promise<void> {
    try {
      this.loading = true;
      let response = await this.userService.removeUserResponsible(userResponsible.email);
      if (response) {
        this.notificationService.showSuccess('Se elimino un usuario.');
        await this.saveResponsiblesInCache();
        this.sessionCacheService.updateData();
      }
      this.loading = false;
    } catch (error) {
      this.loading = false;
      this.notificationService.showError(error.message);
    }
  }

  evaluarEstado(estado: string) {
    switch (estado) {
      case 'A':
        return 'Activo';
      case 'P':
        return 'Pendiente';
      case 'B':
        return 'Dado de baja';
      case 'O':
        return 'Onboarding';
    }
  }
}
